import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import BlockEntrySheet from "../../components/BlockEntrySheet";
import BehaviorSheet from "../../components/BehaviorSheet";
import CommSheet from "../../components/CommSheet";
import ScheduleEditSheet from "../../components/ScheduleEditSheet";
import QuickLogSheet from "../../components/QuickLogSheet";
import GoalCheckSheet from "../../components/GoalCheckSheet";
import { useBranding } from "../../context/BrandingContext";
import ClassroomToday from "./ClassroomToday";
import { PageSkeleton, KpiCard, ProgressBar, EmptyState, StatusBadge } from "../../components/fields";
import { CheckCircle2, Circle, AlertTriangle, MessageSquare, CalendarCheck, Bell, Plus, ChevronRight, Users, BookOpen, ClipboardList, FolderOpen, Contact, Target, School } from "lucide-react";

const weekStartISO = () => {
  const d = new Date();
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d.toISOString().slice(0, 10);
};

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
};

export default function AideHome() {
  const [data, setData] = useState(null);
  const [week, setWeek] = useState(null);
  const [profiles, setProfiles] = useState({});
  const [blockSheet, setBlockSheet] = useState(null);
  const [behaviorSheet, setBehaviorSheet] = useState(null);
  const [commSheet, setCommSheet] = useState(null);
  const [scheduleFor, setScheduleFor] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { branding } = useBranding();
  const [quickLog, setQuickLog] = useState(null);
  const [goalSheet, setGoalSheet] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [view, setView] = useState("students");
  const [goalsDue, setGoalsDue] = useState({});

  const isClassroomAide = user?.aide_type === "classroom";

  const load = () => {
    api.get("/aide/today").then((r) => {
      setData(r.data);
      r.data.students.forEach(({ student }) => {
        api.get(`/students/${student.id}`).then((res) => setProfiles((p) => ({ ...p, [student.id]: res.data })));
      });
    });
    api.get(`/aide/week-stats?week_start=${weekStartISO()}`).then((r) => setWeek(r.data)).catch(() => {});
    api.get("/aide/classroom-today").then((r) => setRooms(r.data.classrooms || [])).catch(() => {});
  };
  useEffect(() => { if (!isClassroomAide) load(); }, [isClassroomAide]);

  useEffect(() => {
    if (!branding?.goal_check_enabled || !data) return;
    data.students.forEach(({ student }) => {
      api.get(`/goals?student_id=${student.id}`).then((r) =>
        setGoalsDue((g) => ({ ...g, [student.id]: r.data.filter((x) => x.due).length }))).catch(() => {});
    });
  }, [branding?.goal_check_enabled, data]);

  if (isClassroomAide) return <ClassroomToday user={user} />;
  if (!data) return <PageSkeleton />;

  if (view === "room") {
    return (
      <div className="space-y-4">
        <RoomTabs rooms={rooms} view={view} setView={setView} />
        <ClassroomToday user={user} />
      </div>
    );
  }

  const nowHM = new Date().toTimeString().slice(0, 5);
  const totalBlocks = data.students.reduce((a, s) => a + s.total_blocks, 0);
  const documented = data.students.reduce((a, s) => a + s.documented_count, 0);
  const docPct = totalBlocks ? Math.round((documented / totalBlocks) * 100) : 0;

  const overdue = [];
  data.students.forEach(({ student, blocks }) => {
    blocks.forEach((b) => { if (!b.documented && b.end < nowHM) overdue.push({ student, block: b }); });
  });
  const unacked = data.students.filter((s) => !s.snapshot_acknowledged && !s.snapshot_empty);
  const alertCount = (overdue.length > 0 ? 1 : 0) + unacked.length;

  const blockStatus = (b) => {
    if (b.documented) return "done";
    if (b.end < nowHM) return "overdue";
    if (b.start <= nowHM && nowHM <= b.end) return "current";
    return "upcoming";
  };

  const openNext = (currentBlockId, student, blocks) => {
    const next = blocks.find((b) => b.block_id !== currentBlockId && !b.documented);
    if (next) setBlockSheet({ student, block: next, blocks });
  };

  const firstName = (user?.name || "").split(" ")[0];

  return (
    <div className="space-y-5 stagger">
      <RoomTabs rooms={rooms} view={view} setView={setView} />
      <div className="fade-up flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-[28px] sm:text-[32px] font-bold tracking-tight text-[#132238] leading-tight">{greeting()}, {firstName}</h1>
          <p className="text-sm text-[#667085] mt-1">Here's what's happening with your student{data.students.length > 1 ? "s" : ""} today.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {data.students.length > 0 && (
            <button data-testid="add-quick-note-btn" onClick={() => setCommSheet(data.students[0].student)}
              className="flex items-center gap-1.5 text-sm font-semibold bg-[#087F5B] hover:bg-[#066548] text-white px-4 py-2.5 rounded-[10px] transition-colors duration-150 active:scale-95">
              <Plus size={16} /> Add Quick Note
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <KpiCard testId="kpi-todays-blocks" label="Today's Blocks" value={totalBlocks} sub={`${documented} complete`} icon={CalendarCheck} tone="green" />
        <KpiCard testId="kpi-doc-today" label="Documentation Today" value={`${docPct}%`} ring={docPct} sub={docPct === 100 ? "All caught up" : `${totalBlocks - documented} remaining`} />
        <KpiCard testId="kpi-week-doc" label="Week Documentation" value={week?.doc_pct != null ? `${week.doc_pct}%` : "—"} sub={week ? `${week.documented} of ${week.scheduled} blocks` : "No data yet"} icon={ClipboardList} tone="blue" />
        <KpiCard testId="kpi-alerts" label="Needs Attention" value={alertCount} sub={alertCount > 0 ? "Requires attention" : "All clear"} icon={alertCount > 0 ? AlertTriangle : CheckCircle2} tone={alertCount > 0 ? "amber" : "green"} />
      </div>

      {data.students.length === 0 && (
        <div className="bg-white border border-[#E5E9EE] rounded-2xl">
          <EmptyState icon={Users} title="No active assignments" hint="Your TAG Admin will assign your student and set their daily schedule — check back soon." />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
        <div className="lg:col-span-2 space-y-5">
          {data.students.map(({ student, blocks, documented_count, total_blocks, snapshot_acknowledged }) => (
            <div key={student.id} data-testid={`student-card-${student.id}`} className="bg-white border border-[#E5E9EE] rounded-2xl shadow-[0_1px_2px_rgba(15,31,56,0.04)] overflow-hidden">
              <div className="px-5 sm:px-6 py-4 border-b border-[#E5E9EE] flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <h2 className="font-heading text-[17px] font-semibold text-[#132238] tracking-tight">Today's Schedule</h2>
                  <p className="text-xs text-[#667085] mt-0.5">{student.first_name} {student.last_name}{student.grade ? ` · Grade ${student.grade}` : ""} · {documented_count} of {total_blocks} documented</p>
                </div>
                <div className="flex items-center gap-2.5">
                  {goalsDue[student.id] > 0 && (
                    <span data-testid={`goals-due-badge-${student.id}`} className="text-[11px] font-bold px-2.5 py-1 rounded-full" style={{ color: "#DB4634", backgroundColor: "#FBE8E5" }}>
                      {goalsDue[student.id]} goal{goalsDue[student.id] > 1 ? "s" : ""} due
                    </span>
                  )}
                  <div className="w-28"><ProgressBar value={total_blocks ? (documented_count / total_blocks) * 100 : 0} /></div>
                </div>
              </div>

              <ul className="px-5 sm:px-6 py-3">
                {blocks.map((b, i) => {
                  const st = blockStatus(b);
                  return (
                    <li key={b.block_id} className="relative flex items-center gap-3.5 py-3">
                      {i < blocks.length - 1 && <span className="absolute left-[9px] top-[38px] bottom-[-10px] w-px bg-[#E5E9EE]" />}
                      {st === "done" ? (
                        <CheckCircle2 size={20} className="text-[#087F5B] shrink-0 relative z-10 bg-white" />
                      ) : st === "overdue" ? (
                        <AlertTriangle size={19} className="text-amber-500 shrink-0 relative z-10 bg-white" />
                      ) : (
                        <Circle size={20} className={`shrink-0 relative z-10 bg-white ${st === "current" ? "text-[#087F5B]" : "text-slate-300"}`} />
                      )}
                      <div className={`flex-1 min-w-0 flex items-center gap-3 rounded-xl px-3 py-2 -mx-1 ${st === "current" ? "bg-[#EAF8F3] ring-1 ring-[#087F5B]/20" : ""}`}>
                        <div className="w-[72px] shrink-0">
                          <p className="text-[13px] font-semibold text-[#132238] font-mono">{b.start}</p>
                          <p className="text-[11px] text-[#98A2B3] font-mono">{b.end}</p>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#132238] truncate">{b.name} {st === "current" && <span className="text-[10px] font-bold text-[#087F5B] ml-1">NOW</span>}</p>
                          <p className="text-xs text-[#667085]">{b.location}</p>
                        </div>
                        {st === "done" ? (
                          <div className="flex items-center gap-2">
                            <StatusBadge tone="success">Documented</StatusBadge>
                            <button data-testid={`document-block-btn-${b.block_id}`} onClick={() => setBlockSheet({ student, block: b, blocks })}
                              className="text-xs font-semibold text-[#667085] hover:text-[#132238] px-2 py-1.5 rounded-lg hover:bg-slate-50 transition-colors duration-150">Edit</button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            {st === "overdue" && <StatusBadge tone="warning">Needs Documentation</StatusBadge>}
                            <button data-testid={`document-block-btn-${b.block_id}`} onClick={() => setBlockSheet({ student, block: b, blocks })}
                              className="text-xs font-semibold bg-[#087F5B] text-white hover:bg-[#066548] px-3.5 py-2 rounded-[10px] transition-colors duration-150 active:scale-95">Document</button>
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>

              <div className="px-5 sm:px-6 py-4 bg-[#F7F9FA] border-t border-[#E5E9EE] flex gap-2.5 flex-wrap">
                <button data-testid={`log-behavior-btn-${student.id}`} onClick={() => setBehaviorSheet(student)}
                  className="flex items-center gap-1.5 text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 px-3.5 py-2.5 rounded-[10px] transition-colors duration-150 active:scale-95">
                  <AlertTriangle size={14} /> Log Behavior / Concern
                </button>
                <button data-testid={`log-comm-btn-${student.id}`} onClick={() => setCommSheet(student)}
                  className="flex items-center gap-1.5 text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 px-3.5 py-2.5 rounded-[10px] transition-colors duration-150 active:scale-95">
                  <MessageSquare size={14} /> Log Communication
                </button>
                <button data-testid={`quick-log-btn-${student.id}`} onClick={() => setQuickLog(student)}
                  className="flex items-center gap-1.5 text-xs font-semibold bg-[#EAF8F3] text-[#066548] border border-[#C8EFE2] hover:bg-[#d9f2e8] px-3.5 py-2.5 rounded-[10px] transition-colors duration-150 active:scale-95">
                  <ClipboardList size={14} /> Quick Log
                </button>
                {branding?.goal_check_enabled && (
                  <button data-testid={`goal-check-btn-${student.id}`} onClick={() => setGoalSheet(student)}
                    className="flex items-center gap-1.5 text-xs font-semibold bg-[#EAF8F3] text-[#066548] border border-[#C8EFE2] hover:bg-[#d9f2e8] px-3.5 py-2.5 rounded-[10px] transition-colors duration-150 active:scale-95">
                    <Target size={14} /> Goal Check
                  </button>
                )}
              </div>
            </div>
          ))}

          {rooms.length > 0 && (
            <button data-testid="whole-class-cta" onClick={() => setView("room")}
              className="w-full text-left border-2 border-dashed border-[#087F5B] rounded-2xl p-5 bg-white hover:bg-[#EAF8F3] transition-colors duration-150 flex items-center gap-4">
              <Users size={22} className="text-[#087F5B] shrink-0" />
              <div>
                <p className="font-heading text-[15px] font-semibold text-[#132238]">{rooms[0].classroom.name} — Log whole-class support</p>
                <p className="text-xs text-[#667085] mt-0.5">When you're helping the room, not one student</p>
              </div>
              <ChevronRight size={18} className="ml-auto text-[#667085]" />
            </button>
          )}
        </div>

        <div className="space-y-5">
          {data.students.map(({ student, snapshot_acknowledged, snapshot_empty }) => {
            const p = profiles[student.id];
            const minis = p ? [
              { label: "Accommodations", value: (p.profile?.accommodations || []).length, icon: BookOpen },
              { label: "Schedule Blocks", value: (p.profile?.schedule || []).length, icon: CalendarCheck },
              { label: "Team Contacts", value: (p.profile?.contacts || []).length, icon: Contact },
              { label: "Documents", value: (p.documents || []).length, icon: FolderOpen },
            ] : [];
            return (
              <div key={student.id} data-testid={`snapshot-card-${student.id}`} className="bg-white border border-[#E5E9EE] rounded-2xl p-5 shadow-[0_1px_2px_rgba(15,31,56,0.04)]">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-heading text-[15px] font-semibold text-[#132238]">Student Snapshot</h3>
                    <p className="text-xs text-[#667085] mt-0.5">{student.first_name} {student.last_name}{student.grade ? ` · Grade ${student.grade}` : ""}</p>
                  </div>
                </div>
                {snapshot_empty ? (
                  <div data-testid={`snapshot-empty-${student.id}`} className="mb-4 rounded-xl border border-[#E5E9EE] bg-[#F7F9FA] p-3.5">
                    <p className="text-[13px] font-semibold text-[#667085]">Snapshot not set up yet</p>
                    <p className="text-xs text-[#98A2B3] mt-0.5 leading-relaxed">Your TAG team hasn't completed {student.first_name}'s Support Snapshot. You can start documenting in the meantime.</p>
                  </div>
                ) : !snapshot_acknowledged && (
                  <div data-testid={`ack-warning-${student.id}`} className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3.5">
                    <p className="text-[13px] font-semibold text-amber-800">Support Snapshot requires review</p>
                    <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">Review {student.first_name}'s current support priorities before beginning today's documentation.</p>
                    <button onClick={() => navigate(`/students/${student.id}`)}
                      className="mt-2.5 text-xs font-semibold bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-lg transition-colors duration-150">Review & Acknowledge</button>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2.5">
                  {minis.map((m) => (
                    <div key={m.label} className="rounded-xl bg-[#F7F9FA] border border-[#E5E9EE] p-3">
                      <m.icon size={14} className="text-[#087F5B]" />
                      <p className="font-heading text-lg font-bold text-[#132238] mt-1.5 leading-none">{m.value}</p>
                      <p className="text-[11px] text-[#667085] mt-1">{m.label}</p>
                    </div>
                  ))}
                </div>
                <button data-testid={`view-snapshot-btn-${student.id}`} onClick={() => navigate(`/students/${student.id}`)}
                  className="mt-4 w-full flex items-center justify-center gap-1 text-xs font-semibold text-[#087F5B] bg-[#EAF8F3] hover:bg-[#d9f2e8] px-3 py-2.5 rounded-[10px] transition-colors duration-150">
                  View Profile <ChevronRight size={14} />
                </button>
                <button data-testid={`view-insights-btn-${student.id}`} onClick={() => navigate(`/students/${student.id}/dashboard`)}
                  className="mt-2 w-full text-xs font-medium text-[#667085] hover:text-[#132238] py-1.5 transition-colors duration-150">View insights & trends</button>
              </div>
            );
          })}

          <div data-testid="alerts-card" className="bg-white border border-[#E5E9EE] rounded-2xl p-5 shadow-[0_1px_2px_rgba(15,31,56,0.04)]">
            <h3 className="font-heading text-[15px] font-semibold text-[#132238] flex items-center gap-2 mb-3">
              Alerts {alertCount > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{alertCount}</span>}
            </h3>
            {alertCount === 0 ? (
              <div className="flex items-center gap-2.5 text-sm text-[#667085]">
                <CheckCircle2 size={16} className="text-[#087F5B]" /> You're all caught up.
              </div>
            ) : (
              <div className="space-y-2.5">
                {overdue.length > 0 && (
                  <div data-testid="eod-reminder-banner" className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 p-3">
                    <Bell size={14} className="text-amber-600 shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <p className="text-[13px] font-semibold text-amber-800">{overdue.length} block{overdue.length > 1 ? "s" : ""} past due</p>
                      <p className="text-[11px] text-amber-700 truncate">{overdue.map((o) => o.block.name).join(", ")}</p>
                    </div>
                  </div>
                )}
                {unacked.map(({ student }) => (
                  <button key={student.id} onClick={() => navigate(`/students/${student.id}`)}
                    className="w-full flex items-start gap-2.5 rounded-xl border border-blue-200 bg-blue-50 p-3 text-left hover:bg-blue-100/60 transition-colors duration-150">
                    <BookOpen size={14} className="text-blue-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[13px] font-semibold text-blue-800">Support Snapshot needs review</p>
                      <p className="text-[11px] text-blue-700">{student.first_name} {student.last_name}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div data-testid="week-progress-card" className="bg-white border border-[#E5E9EE] rounded-2xl p-5 shadow-[0_1px_2px_rgba(15,31,56,0.04)]">
            <h3 className="font-heading text-[15px] font-semibold text-[#132238] mb-4">Week-to-Date Progress</h3>
            {week && week.documented > 0 ? (
              <div className="space-y-4">
                {[["Documentation", week.doc_pct, "green"], ["Independence", week.independence_pct, "blue"], ["Positive Behavior", week.regulated_pct, "green"]].map(([label, val, tone]) => (
                  <div key={label}>
                    <div className="flex justify-between text-[13px] mb-1.5">
                      <span className="font-medium text-[#132238]">{label}</span>
                      <span className="font-semibold text-[#667085]">{val != null ? `${val}%` : "—"}</span>
                    </div>
                    <ProgressBar value={val || 0} tone={tone} height="h-1.5" />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[13px] text-[#667085]">Progress appears here once you document your first blocks this week.</p>
            )}
          </div>
        </div>
      </div>

      {blockSheet && (
        <BlockEntrySheet
          student={blockSheet.student}
          block={blockSheet.block}
          date={data.date}
          hasNext={blockSheet.blocks?.some((b) => b.block_id !== blockSheet.block.block_id && !b.documented)}
          onClose={() => setBlockSheet(null)}
          onSaved={(goNext) => {
            const ctx = blockSheet;
            setBlockSheet(null);
            load();
            if (goNext) openNext(ctx.block.block_id, ctx.student, ctx.blocks);
          }}
        />
      )}
      {scheduleFor && (
        <ScheduleEditSheet
          student={scheduleFor}
          onClose={() => { setScheduleFor(null); load(); }}
          onSaved={() => { setScheduleFor(null); load(); }}
        />
      )}
      {behaviorSheet && <BehaviorSheet student={behaviorSheet} onClose={() => setBehaviorSheet(null)} onSaved={() => { setBehaviorSheet(null); load(); }} />}
      {commSheet && <CommSheet student={commSheet} onClose={() => setCommSheet(null)} onSaved={() => setCommSheet(null)} />}
      {quickLog && <QuickLogSheet student={quickLog} onClose={() => setQuickLog(null)} />}
      {goalSheet && <GoalCheckSheet student={goalSheet} onClose={() => setGoalSheet(null)} />}
    </div>
  );
}

const RoomTabs = ({ rooms, view, setView }) => {
  if (!rooms.length) return null;
  return (
    <div className="flex gap-2">
      {[["students", "My Students", Users], ["room", rooms[0].classroom.name, School]].map(([k, label, Icon]) => (
        <button key={k} data-testid={`aide-view-tab-${k}`} onClick={() => setView(k)}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-150 ${
            view === k ? "bg-[#087F5B] text-white shadow-sm" : "bg-white text-[#667085] border border-[#E5E9EE] hover:bg-slate-50"}`}>
          <Icon size={15} /> {label}
        </button>
      ))}
    </div>
  );
};
